import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiMinusCircleLight,
} from 'react-icons/pi';
import BrandLoader from '../BrandLoader';
import { AIStage } from './aiRun';

/**
 * The stage rail: what the engine has reported, in the order it reported it.
 *
 * **Icons come from STATUS, not from the stage's name.** The tracker this
 * replaces sniffed 60 lines of `stepName.toLowerCase().includes(…)` to pick a
 * glyph, so any copy change in the engine silently fell through to a generic
 * document icon — a wrong icon rendered with total confidence. A per-key icon
 * map is available for callers that want flavour (`iconForKey`), and a key it
 * doesn't know degrades to the neutral ring rather than to a wrong picture.
 */

const StageGlyph: React.FC<{ status: AIStage['status'] }> = ({ status }) => {
  if (status === 'done') {
    return <PiCheckCircleLight className="w-4 h-4 text-success-500" aria-hidden />;
  }
  if (status === 'failed') {
    return <PiWarningCircleLight className="w-4 h-4 text-danger-500" aria-hidden />;
  }
  if (status === 'skipped') {
    return <PiMinusCircleLight className="w-4 h-4 text-fg-muted" aria-hidden />;
  }
  if (status === 'pending') {
    return <span className="block w-4 h-4 rounded-full border border-hairline" aria-hidden />;
  }
  return <BrandLoader size="glyph" />;
};

export interface AIStageListProps {
  stages: AIStage[];
  /**
   * Optional flavour glyph per stage key. A key that isn't in the map keeps
   * the status glyph — never a guessed one.
   */
  iconForKey?: Record<string, React.ReactNode>;
  /**
   * Draw a hairline rail between the glyphs, the way the chronology surfaces
   * do. Opt-in: it turns a list of lines into one continuous progression,
   * which is what a five-stage run wants and what a two-line index build
   * doesn't.
   */
  connector?: boolean;
  /**
   * `showcase` is the reading for a rail that IS the screen: rows sit further
   * apart, the lines inside a row are separated, and each tier gets a size and
   * a tone of its own instead of three near-identical greys stacked tight.
   * `default` is unchanged — the dense reading a two-line index build wants.
   */
  density?: 'default' | 'showcase';
  /**
   * Last-mile polish on the engine's own sentence — see
   * `Matters/matterProgressCopy`. Identity when a caller passes nothing, so
   * the engine's words still reach the screen verbatim by default.
   */
  formatMessage?: (message: string) => string;
  className?: string;
}

const AIStageList: React.FC<AIStageListProps> = ({
  stages,
  iconForKey,
  connector = false,
  density = 'default',
  formatMessage,
  className,
}) => {
  const { t } = useTranslation();
  if (!stages.length) return null;

  const showcase = density === 'showcase';

  const polish = (text: string) => (formatMessage ? formatMessage(text) : text);

  return (
    <ol
      className={clsx(connector ? 'relative' : 'space-y-2', className)}
      data-testid="ai-stage-list"
    >
      {stages.map((stage, i) => (
        <li
          key={stage.key}
          data-testid="ai-stage"
          data-stage={stage.key}
          data-status={stage.status}
          className={clsx(
            'reveal-item flex items-start gap-2 text-xs',
            connector && 'relative gap-2.5 pb-3 last:pb-0',
            // A rail that IS the screen: rows breathe, and the glyph sits
            // further off its text so the hairline reads as a spine.
            showcase && connector && 'gap-4 pb-6 last:pb-0',
          )}
          style={{ '--reveal-delay': `${Math.min(i, 8) * 60}ms` } as React.CSSProperties}
        >
          {/* The rail joins this glyph to the next one, and stops at the last. */}
          {connector && i < stages.length - 1 && (
            <span
              aria-hidden
              className="absolute left-2 top-5 bottom-0 w-px bg-hairline-soft"
            />
          )}
          <span className="mt-0.5 shrink-0 [&>svg]:h-4 [&>svg]:w-4">
            {stage.status === 'active' && iconForKey?.[stage.key]
              ? iconForKey[stage.key]
              : <StageGlyph status={stage.status} />}
          </span>
          <span className={clsx('min-w-0 flex-1 break-words', showcase && 'space-y-1.5')}>
            <span
              className={clsx(
                'text-sm font-medium',
                stage.status !== 'active' && stage.status !== 'failed'
                  ? 'text-fg-muted'
                  : stage.status === 'failed'
                    ? 'text-danger-600 dark:text-danger-400'
                    : 'text-fg-strong',
              )}
            >
              {t(stage.label)}
            </span>
            {/* Tier one: the engine's own words, verbatim. */}
            {stage.message && stage.message !== stage.label && (
              <span
                className={clsx(
                  'block',
                  showcase ? 'text-sm leading-relaxed text-fg-body' : 'text-xs text-fg-muted',
                )}
              >
                {polish(stage.message)}
              </span>
            )}
            {/* Tier two: the optional aside, one notch quieter than the line it
                explains — quieter in TONE, not in legibility. The old
                `text-[11px] text-fg-muted/70` was a grey on glass nobody could
                read; a showcase rail keeps the full muted token at body size.
                Absent for engines that don't send `narration`. */}
            {stage.narration && stage.narration !== stage.message && (
              <span
                className={clsx(
                  'block',
                  showcase ? 'text-xs leading-relaxed text-fg-muted' : 'text-[11px] text-fg-muted/70',
                )}
              >
                {polish(stage.narration)}
              </span>
            )}
            {stage.error && (
              <span
                className={clsx(
                  'block text-danger-600 dark:text-danger-400',
                  showcase ? 'text-xs leading-relaxed' : 'text-[11px]',
                )}
              >
                {stage.error}
              </span>
            )}
            {/* Counts the engine reported. Nothing here is derived. In a
                showcase rail they are chips rather than a third grey sentence,
                so they read as data beside the prose instead of more of it. */}
            {stage.facts && stage.facts.length > 0 && (
              showcase ? (
                <span className="flex flex-wrap items-center gap-1.5 pt-0.5">
                  {stage.facts.map((f) => (
                    <span
                      key={`${f.label}-${f.value}`}
                      className="inline-flex items-center gap-1 rounded-full bg-surface-sunken/70 border border-hairline-soft px-2 py-0.5 text-[11px] text-fg-muted"
                    >
                      <span className="font-medium text-fg-body tabular-nums">{f.value}</span>
                      {t(f.label)}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="mt-0.5 block text-[11px] text-fg-muted tabular-nums">
                  {stage.facts.map((f) => `${f.value} ${t(f.label)}`).join(' · ')}
                </span>
              )
            )}
          </span>
        </li>
      ))}
    </ol>
  );
};

export default AIStageList;
